import React, { useState, memo } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import StatusBadge from './StatusBadge';
import type { MachineCardProps } from '../../types/supply-gpu';
import styles from '../../styles/components/supply-gpu/MachineCard.module.css';

const MachineCard: React.FC<MachineCardProps> = memo(({
  machine,
  onCopyCommand,
  onMenuAction,
  className,
}) => {
  const [isCopying, setIsCopying] = useState(false);

  const handleCopyCommand = async () => {
    if (!machine.connectionCommand || !onCopyCommand) return;

    setIsCopying(true);
    try {
      await onCopyCommand(machine.connectionCommand);
    } finally {
      setIsCopying(false);
    }
  };

  const handleMenuClick = (action: string) => {
    if (onMenuAction) {
      onMenuAction(action, machine.id);
    }
  };

  const formatMetricValue = (value: number, unit: string) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K ${unit}`;
    }
    return `${value} ${unit}`;
  };

  const renderMetrics = () => {
    if (!machine.specs) {
      return (
        <div 
          className={styles.emptyMetrics}
          role="status"
          aria-live="polite"
        >
          Performance metrics will appear once connected
        </div>
      );
    }

    const { tflops, memory, cudaVersion, uploadSpeed, downloadSpeed } = machine.specs;

    return (
      <div className={styles.metrics} role="list" aria-label="Performance metrics">
        <div className={styles.metric} role="listitem">
          <span className={styles.metricLabel} id={`tflops-${machine.id}`}>TFLOPS</span>
          <span 
            className={styles.metricValue}
            aria-labelledby={`tflops-${machine.id}`}
            aria-describedby={`tflops-${machine.id}`}
          >
            {tflops.toFixed(1)}
          </span>
        </div>
        <div className={styles.metric} role="listitem">
          <span className={styles.metricLabel} id={`memory-${machine.id}`}>Memory</span>
          <span 
            className={styles.metricValue}
            aria-labelledby={`memory-${machine.id}`}
            aria-describedby={`memory-${machine.id}`}
          >
            {memory} GB
          </span>
        </div>
        <div className={styles.metric} role="listitem">
          <span className={styles.metricLabel} id={`cuda-${machine.id}`}>CUDA Version</span>
          <span 
            className={styles.metricValue}
            aria-labelledby={`cuda-${machine.id}`}
            aria-describedby={`cuda-${machine.id}`}
          >
            {cudaVersion}
          </span>
        </div>
        <div className={styles.metric} role="listitem">
          <span className={styles.metricLabel} id={`upload-${machine.id}`}>Upload Speed</span>
          <span 
            className={styles.metricValue}
            aria-labelledby={`upload-${machine.id}`}
            aria-describedby={`upload-${machine.id}`}
          >
            {formatMetricValue(uploadSpeed, 'Mbps')}
          </span>
        </div>
        <div className={styles.metric} role="listitem">
          <span className={styles.metricLabel} id={`download-${machine.id}`}>Download Speed</span>
          <span 
            className={styles.metricValue}
            aria-labelledby={`download-${machine.id}`}
            aria-describedby={`download-${machine.id}`}
          >
            {formatMetricValue(downloadSpeed, 'Mbps')}
          </span>
        </div>
      </div>
    );
  };

  const renderConnectionCommand = () => {
    if (machine.status !== 'pending' || !machine.connectionCommand) {
      return null;
    }

    return (
      <div 
        className={styles.connectionCommand}
        role="region"
        aria-labelledby={`command-label-${machine.id}`}
      >
        <div className={styles.commandHeader}>
          <span 
            className={styles.commandLabel}
            id={`command-label-${machine.id}`}
          >
            Connection Command
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyCommand}
            disabled={isCopying}
            className={styles.copyButton}
            aria-label={`Copy connection command for ${machine.model}`}
            aria-describedby={`command-text-${machine.id}`}
          >
            {isCopying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
        <div 
          className={styles.commandText}
          id={`command-text-${machine.id}`}
          role="textbox"
          aria-readonly="true"
          aria-label="Connection command text"
          tabIndex={0}
        >
          {machine.connectionCommand}
        </div>
      </div>
    );
  };

  const renderMenuButton = () => {
    return (
      <button
        className={styles.menuButton}
        onClick={() => handleMenuClick('menu')}
        aria-label={`More actions for ${machine.model}`}
        aria-haspopup="menu"
        aria-expanded="false"
        type="button"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="8" cy="3" r="1.5" fill="currentColor" />
          <circle cx="8" cy="8" r="1.5" fill="currentColor" />
          <circle cx="8" cy="13" r="1.5" fill="currentColor" />
        </svg>
      </button>
    );
  };

  return (
    <Card
      variant="default"
      padding="none"
      interactive
      className={`${styles.machineCard} ${className || ''}`}
      role="article"
      aria-labelledby={`machine-${machine.id}-title`}
      aria-describedby={`machine-${machine.id}-status`}
    >
      <div className={styles.header}>
        <div>
          <h3 
            id={`machine-${machine.id}-title`}
            className={styles.modelName}
          >
            {machine.model}
          </h3>
          <div id={`machine-${machine.id}-status`}>
            <StatusBadge status={machine.status} />
          </div>
        </div>
        {renderMenuButton()}
      </div>

      <div 
        role="region" 
        aria-labelledby={`machine-${machine.id}-title`}
        aria-label="Machine performance metrics"
      >
        {renderMetrics()}
      </div>
      {renderConnectionCommand()}
    </Card>
  );
});

MachineCard.displayName = 'MachineCard';

export default MachineCard;